import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setAiGa4Event } from '../../../services/event-tracking/actions'
import { AI_MESSAGE_SENT_GA4_EVENT } from '../../../services/event-tracking/index'
import { selectAuth } from '../../../services/auth/selectors'
import { selectUserLanguagePrimary } from '../../../services/user/index'
import { usePostHogEvent } from '../../PostHog/index'
import { FloatingCard } from '../../OracleCommons'
import { useTranslatedText } from '../../OracleCommons/StaticTextContext'
import {
  usePersonalizedSeedQuestionListQuery,
  useTarotGreetingUserQuery,
} from '../queries.generated'
import { resolveGreetingName } from './resolveGreetingName'

interface OracleGreetingCardProps {
  sendChat: (message: string) => void
  children: React.ReactNode
}

export const OracleGreetingCard: React.FC<OracleGreetingCardProps> = ({
  sendChat,
  children,
}) => {
  const { data } = useTarotGreetingUserQuery()
  const displayName = data?.userProfileCurrent?.displayName ?? null

  const welcomeBackWithName = useTranslatedText({
    keys: ['oracleGreetingCard', 'welcomeBackWithName'],
    params: { name: displayName ?? '' },
  })
  const welcomeBackNoName = useTranslatedText({
    keys: ['oracleGreetingCard', 'welcomeBackNoName'],
  })

  const heading = resolveGreetingName(displayName)
    ? welcomeBackWithName
    : welcomeBackNoName

  return (
    <FloatingCard className="oracle-greeting-card">
      <h2 className="oracle-greeting-card__heading">{heading}</h2>
      <SeedPillRow sendChat={sendChat} />
      <div className="oracle-greeting-card__input-slot">{children}</div>
    </FloatingCard>
  )
}

interface SeedPillRowProps {
  sendChat: (message: string) => void
}

const SeedPillRow: React.FC<SeedPillRowProps> = ({ sendChat }) => {
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const language = useSelector(selectUserLanguagePrimary)
  const trackMessageSent = usePostHogEvent('ai_message_sent')

  const { data, loading, error } = usePersonalizedSeedQuestionListQuery({
    variables: { limit: 6, language },
    context: { jwt: auth.get('jwt') },
  })

  const seedQuestions = data?.userInfoCurrent?.userInfo?.seedQuestions ?? []

  if (loading || error || seedQuestions.length === 0) {
    return null
  }

  const handlePillClick = (question: string) => {
    sendChat(question)
    trackMessageSent(question, 'Prompt', 'Prompt')
    dispatch(setAiGa4Event(AI_MESSAGE_SENT_GA4_EVENT.mergeDeep({
      ui_element: 'Prompt',
      input_method: 'prompt',
    })))
  }

  return (
    <div className="oracle-greeting-card__pill-row">
      {seedQuestions.map((pill) => (
        <button
          key={pill.id}
          type="button"
          className="oracle-greeting-card__pill"
          onClick={() => handlePillClick(pill.question)}
        >
          {pill.question}
        </button>
      ))}
    </div>
  )
}
